/**
 * Reports List Page
 *
 * MUI re-implementation of the classic Reports list (`src/app/routes/reports/containers/list.js`).
 * Uses SmartDataGrid for sorting/filtering/pagination + user preferences (column visibility,
 * pinning, density, ordering). Mirrors the patterns used by LocationList / OrganizationsList.
 *
 * The Add/Edit wizard opens in the global side drawer (AddDrawerProvider), and per-row
 * actions (Edit, Copy, Email, View/Download) are accessed via the action menu in each row.
 */

import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Menu, MenuItem, Snackbar, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FormattedMessage, useIntl } from 'react-intl';

import MainCard from 'ui-component/cards/MainCard';
import SmartDataGrid from '../../../ui-component/dataGrid/SmartDataGrid';
import EllipsisTooltip from '../../../commonComponent/EllipsisTooltip';
import { useDrawer } from '../../../contexts/AddDrawerContext';
import useConfig from '../../../hooks/useConfig';
import { FEATURE_IDS, useFeatureEnabled, useFeatureListLoaded } from '../../../utils/supportedFeatures';
import cancelAutoRenewalImg from '../../../assets/images/cancel-auto-renewal.svg';
import {
  deleteReport,
  downloadBrowsingHistoryReport,
  formatReportDate,
  REPORT_TEMPLATE_TYPES,
  SCHEDULE_LABELS
} from '../../../api/reportsService';
import {
  CP_REPORT_STATE,
  classifyCaptivePortalStatus,
  getCaptivePortalReportState,
  getCaptivePortalDownloadUrl,
  cancelCaptivePortalSchedule
} from '../../../api/captivePortalReportService';
import ConfirmDialog from '../../../commonComponent/ConfirmDialog';
import roleManagement from '../../../utils/roleManagement';
import EditActionButton from '../../../commonComponent/EditActionButton';

const AddEditReportWizard = lazy(() => import('./AddEditReportDialog'));
const CopyReportDialog = lazy(() => import('./CopyReportDialog'));
const EmailReportDialog = lazy(() => import('./EmailReportDialog'));
const ViewDownloadReportDialog = lazy(() => import('./ViewDownloadReportDialog'));
const ViewReportDialog = lazy(() => import('./ViewReportDialog'));

// Headers automatically attached by axiosInstance for the Reports API.
// Returned from `apiConfig.getHeaders` so SmartDataGrid forwards them on every request.
const REPORTS_HEADERS = ['accountId', 'token', 'apiKey'];

// AppCom ReportType.CAPTIVE_PORTAL(4). Captive-portal reports are generated
// asynchronously by insight-job-service, so their rows poll live status and
// download via a freshly-minted presigned URL rather than AppCom's pdfList.
const CAPTIVE_PORTAL_TEMPLATE_TYPE = '4';

// Delete API responseCode (with status false) when the report no longer exists,
// e.g. it was already deleted from another session.
const REPORT_ALREADY_DELETED_CODE = '20003';

const TEMPLATE_TYPE_LABELS = {
  1: 'Troubleshooting',
  2: 'Custom Report',
  3: 'Browsing History',
  4: 'Captive Portal'
};

const isBrowsingHistoryRow = (row) =>
  String(row?.templateTypeCode ?? row?.raw?.templateType ?? '') === REPORT_TEMPLATE_TYPES.BROWSING_HISTORY;

const formatBrowsingHistoryZipName = (reportName) => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const base = (reportName || 'report').replace(/\.zip$/i, '');
  return `${base}_${stamp}.zip`;
};

const triggerBrowsingHistoryZipDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Live-status polling cadence for in-flight captive-portal report jobs.
const CP_POLL_INTERVAL_MS = 5000;
const CP_POLL_MAX_TICKS = 60; // ~5 min ceiling, then stop polling
// Status lookups issued at a time when preparing a CSV export (up to 200 rows).
const CP_EXPORT_FETCH_BATCH_SIZE = 6;

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <CircularProgress size={28} />
  </Box>
);

/** True when a CP row runs on a recurring schedule (so it has no single "last run"). */
const isScheduledCaptivePortalRow = (row) => !!row?.schedule && !['N/A', 'Run Once', ''].includes(row.schedule);

// Export text per CP_REPORT_STATE — same labels as the chips below.
const CP_STATE_EXPORT_LABELS = {
  [CP_REPORT_STATE.COMPLETED]: { id: 'completed', defaultMessage: 'Completed' },
  [CP_REPORT_STATE.FAILED]: { id: 'failed', defaultMessage: 'Failed' },
  [CP_REPORT_STATE.IN_PROGRESS]: { id: 'inProgress', defaultMessage: 'In Progress' }
};

/**
 * "Last Report" as plain text for the CSV: a date for non-CP rows, the chip's
 * status for CP rows (whose `lastReport` is empty and formats to "N/A").
 * Prefers the fetched status, since the list API omits `cpLastStatus`.
 */
const getLastReportExportValue = (row, intl, cpStateByJobId) => {
  if (!row?.isCaptivePortal) return row?.lastReport ?? '';
  if (isScheduledCaptivePortalRow(row)) {
    return intl.formatMessage({ id: 'scheduledCommon', defaultMessage: 'Scheduled' });
  }
  const state = cpStateByJobId?.get(row.cpJobId) || classifyCaptivePortalStatus(row.cpLastStatus);
  return intl.formatMessage(CP_STATE_EXPORT_LABELS[state]);
};

/**
 * Live status chip for a captive-portal report row. Seeds from the stored
 * `cpLastStatus`, then polls insight-job-service (via AppCom) until the job
 * reaches a terminal state — so the list reflects "In Progress" → "Completed" /
 * "Failed" without a manual refresh. Polling stops on terminal state, unmount,
 * or the tick ceiling.
 */
const CaptivePortalStatusCell = ({ jobId, initialStatus }) => {
  const [state, setState] = useState(() => classifyCaptivePortalStatus(initialStatus));

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    let ticks = 0;
    let timer = null;

    const tick = async () => {
      try {
        const next = await getCaptivePortalReportState(jobId);
        if (cancelled) return;
        setState(next);
        if (next !== CP_REPORT_STATE.IN_PROGRESS) return; // terminal — stop
      } catch (err) {
        // transient (network / not-yet-visible) — keep polling within budget
      }
      ticks += 1;
      if (cancelled || ticks >= CP_POLL_MAX_TICKS) return;
      timer = setTimeout(tick, CP_POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);

  if (state === CP_REPORT_STATE.COMPLETED) {
    return (
      <Chip
        size="small"
        color="success"
        variant="outlined"
        label={<FormattedMessage id="completed" defaultMessage="Completed" />}
        id={`reportList-cpStatus-completed-${jobId}`}
      />
    );
  }
  if (state === CP_REPORT_STATE.FAILED) {
    return (
      <Chip
        size="small"
        color="error"
        variant="outlined"
        label={<FormattedMessage id="failed" defaultMessage="Failed" />}
        id={`reportList-cpStatus-failed-${jobId}`}
      />
    );
  }
  return (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      icon={<CircularProgress size={12} thickness={6} sx={{ color: 'inherit' }} />}
      label={<FormattedMessage id="inProgress" defaultMessage="In Progress" />}
      id={`reportList-cpStatus-inProgress-${jobId}`}
    />
  );
};

/**
 * API Configuration factory for Reports SmartDataGrid.
 * The PUT /reportlist API expects a body of `{ sortBy, sortDir, dataFilter }` and
 * returns `{ info: [...], details: { muiPaginationLimit, count, allowUserToCreateNewReport } }`.
 *
 * Sort field mapping — only `name`, `description`, `createdAt` are supported by
 * the backend. UI columns map to those keywords; columns without a mapping are
 * marked `sortable: false`.
 */
const createApiConfig = () => ({
  apiKey: 'REPORT_LIST',
  userPreferenceAPIKey: 'customReportList',
  pagerSize: (data) => data?.details?.muiPaginationLimit || 200,

  transformResponse: (data) =>
    (data?.info || []).map((row) => {
      const reportId = row?.reportTemplateId || row?.reportId || row?.id || row?._id || '';
      const isCaptivePortal = String(row?.templateType) === CAPTIVE_PORTAL_TEMPLATE_TYPE;
      // CP scheduled rows are saved as { type:'2'(=Scheduled), range:<frequency> }.
      // The repeat-type code '2' collides with the Weekly frequency code, so for a
      // scheduled CP row the real frequency lives in `range` — read it there;
      // otherwise the type field already carries the schedule/frequency code.
      const rawScheduleType = row?.schedule?.type || row?.scheduleType || row?.schedule || '';
      // For CP scheduled rows the frequency lives in schedule.range (nested) or
      // scheduleRepeats (flat). Both shapes must be handled because the edit path
      // already reads tpl.schedule?.range ?? tpl.scheduleRepeats for the same field.
      const cpFrequency = row?.schedule?.range || row?.scheduleRepeats;
      const scheduleCode = isCaptivePortal && String(rawScheduleType) === '2' && cpFrequency ? cpFrequency : rawScheduleType;
      return {
        id: String(reportId),
        reportId: String(reportId),
        reportName: row?.reportName || row?.name || '',
        description: row?.description || '',
        // Captive-portal rows carry the async job id + last-known status so the
        // list can poll live state and mint a fresh presigned download URL.
        // Accept either field name: AppCom returns `cpJobId`, but the save DTO
        // (buildCpSavePayload) sends `jobId`, so a response that echoes the
        // request shape would otherwise leave the id empty and break polling.
        isCaptivePortal,
        cpJobId: row?.cpJobId || row?.jobId || '',
        // For a SCHEDULED CP row, cpJobId is actually the recurring scheduleId.
        // Surface it separately so delete can cancel the cron (AppCom report-delete
        // doesn't touch insight-job-service). Empty for one-shot CP / non-CP rows —
        // those have no recurring schedule to cancel.
        cpScheduleId: isCaptivePortal && String(rawScheduleType) === '2' ? row?.cpJobId || row?.jobId || '' : '',
        cpLastStatus: row?.cpLastStatus || '',
        schedule: SCHEDULE_LABELS[String(scheduleCode)] || 'N/A',
        createdOn: formatReportDate(
          (row?.createdOn ? row.createdOn / 1000 : 0) || (row?.createdAt ? row.createdAt / 1000 : 0) || row?.creationTime
        ),
        lastReport: formatReportDate((row?.lastReport ? row.lastReport / 1000 : 0) || row?.lastReportTime),
        nextReport: formatReportDate((row?.nextReport ? row.nextReport / 1000 : 0) || row?.nextReportTime),
        templateType: TEMPLATE_TYPE_LABELS[row?.templateType] || 'N/A',
        templateTypeCode: String(row?.templateType ?? ''),
        emails: row?.emails || row?.emailRecipients || [],
        raw: row
      };
    }),

  getTotalCount: (data) => data?.details?.count ?? 0,

  // {searchText} placeholder in URL — must be either "" or "?searchText=<v>".
  getParams: (page, limit, searchText) => ({
    page,
    searchText: searchText ? `?searchText=${encodeURIComponent(searchText)}` : ''
  }),

  /**
   * Build the PUT body. Backend supports:
   *   - sortBy: "name" | "description" | "createdAt"
   *   - sortDir: "asc" | "desc"
   *   - dataFilter: "name", "description", "templateType" accepted as field
   */
  getBody: (sortModel, filters) => {
    const data = {};
    if (sortModel && sortModel.length > 0) {
      const fieldMap = {
        reportName: 'name',
        description: 'description',
        createdOn: 'createdAt'
      };
      const mapped = fieldMap[sortModel[0].field];
      if (mapped) {
        data.sortBy = mapped;
        data.sortDir = sortModel[0].sort;
      }
    }
    if (filters && filters.length > 0) {
      data.dataFilter = filters
        .filter((f) => f.field && f.value !== undefined && f.value !== '')
        .map((f) => ({
          field: f.field,
          operator: f.operator || '=',
          value: f.value
        }));
    }
    return Object.keys(data).length > 0 ? { data } : {};
  },

  getHeaders: () => REPORTS_HEADERS
});

const Reports = () => {
  const intl = useIntl();
  const { setOrgDrawerOpen, setDrawerContent, setTitle, setOnCloseCallback } = useDrawer();
  const { showLoader, hideLoader } = useConfig();

  // Captive Portal (ICP) reports feature flag (InsightFeatureAccess code 'CAPTIVE_PORTAL_REPORTING').
  // When disabled, CP reports cannot be created (radio hidden in ReportProfileStep)
  // nor edited from this list. Gate on featureListLoaded so the transient `false`
  // during cache hydration doesn't briefly block editing valid CP reports.
  const captivePortalReportsEnabled = useFeatureEnabled(FEATURE_IDS.CAPTIVE_PORTAL_REPORTS);
  const featureListLoaded = useFeatureListLoaded();
  const cpReportsDisabled = featureListLoaded && !captivePortalReportsEnabled;

  const [allowUserToCreateNewReport, setAllowUserToCreateNewReport] = useState(null);
  const [limitWarningOpen, setLimitWarningOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [currentPageIds, setCurrentPageIds] = useState([]);
  const [currentPageRows, setCurrentPageRows] = useState([]);
  const justChangedPageRef = useRef(false);
  // Accumulates reportId -> cpScheduleId across every page the grid has shown.
  // Bulk selection persists across pages, so a delete may include scheduled CP
  // rows that aren't on the current page; this ref lets us still cancel their
  // crons. (Rows must have been viewed on their page to be selected, so they're
  // captured here by the time of delete.)
  const scheduleIdByReportIdRef = useRef(new Map());
  // Accumulates rowId -> isCaptivePortal across every page the grid has shown.
  // Cross-page selection means a selected CP row may not be on currentPageRows
  // when Edit is invoked; this lets the CP-disabled guard resolve the type
  // regardless of page (and fail closed when the id was never seen).
  const isCaptivePortalByIdRef = useRef(new Map());
  // cpJobId -> finished CP_REPORT_STATE. Cache for the export; only terminal
  // states are stored, so a repeat download refetches jobs still running.
  const cpStateByJobIdRef = useRef(new Map());

  /**
   * Fetches the real status of every one-shot CP row before a CSV export.
   * The export spans all fetched rows (200 per block, 25 shown), so most never
   * rendered a status cell. Batched to spare the job service; a failed lookup is
   * skipped so the getter falls back instead of breaking the download.
   */
  const prepareCpStatusesForExport = useCallback(async (rows) => {
    const pending = [
      ...new Set(
        (rows || [])
          .filter((row) => row?.isCaptivePortal && !isScheduledCaptivePortalRow(row) && row.cpJobId)
          .map((row) => row.cpJobId)
          .filter((jobId) => !cpStateByJobIdRef.current.has(jobId))
      )
    ];
    for (let i = 0; i < pending.length; i += CP_EXPORT_FETCH_BATCH_SIZE) {
      await Promise.all(
        pending.slice(i, i + CP_EXPORT_FETCH_BATCH_SIZE).map(async (jobId) => {
          try {
            const state = await getCaptivePortalReportState(jobId);
            // Cache only a finished job. Caching "In Progress" would keep
            // exporting it after the job completes, since the cache is hit
            // before the lookup. Uncached rows fall back to "In Progress".
            if (state !== CP_REPORT_STATE.IN_PROGRESS) {
              cpStateByJobIdRef.current.set(jobId, state);
            }
          } catch (err) {
            // leave unset — getLastReportExportValue falls back to cpLastStatus
          }
        })
      );
    }
  }, []);

  // Per-row action menu (anchor element drives popover positioning).
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // Modal state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState([]);
  const [copyTarget, setCopyTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [reportToView, setReportToView] = useState(null);

  const apiConfig = React.useMemo(() => {
    const base = createApiConfig();
    return {
      ...base,
      onRawDataLoaded: (rawData, serverPage) => {
        const remaining = rawData?.details?.allowUserToCreateNewReport;
        if (remaining !== undefined && remaining !== null) {
          setAllowUserToCreateNewReport(Number(remaining));
        }
        // Keep the selection in step with the list the grid now shows: after a
        // Refresh, search or filter, drop selected ids the response no longer
        // contains (e.g. deleted from another session, or hidden by the search),
        // so Add/Edit/Delete never act on a row the user can't see. Only safe
        // when this response holds the whole result set — otherwise a missing id
        // may just be on another server block.
        const rows = base.transformResponse(rawData);
        const total = base.getTotalCount(rawData);
        if ((serverPage ?? 0) !== 0 || rows.length < total) return;
        const existingIds = new Set(rows.map((r) => r.id));
        setSelectedRow((prev) => {
          const kept = prev.filter((id) => existingIds.has(String(id)));
          return kept.length === prev.length ? prev : kept;
        });
      }
    };
  }, []);

  const getReportLimitWarningMessage = useCallback(() => {
    const role = roleManagement.getUserRole();
    const isAdmin = role === 'admin' || role === 'secondaryAdmin';
    return isAdmin
      ? intl.formatMessage({
          id: 'reportGenerationLimitReachedAdmin',
          defaultMessage:
            'You have reached the maximum allowed limit of 50 reports. Please delete one or more existing reports before generating a new report.'
        })
      : intl.formatMessage({
          id: 'reportGenerationLimitReachedManager',
          defaultMessage:
            'You have reached the maximum report generation limit. Please try again later or contact your administrator if you need additional reports.'
        });
  }, [intl]);

  const showReportLimitWarningIfNeeded = useCallback(() => {
    if (allowUserToCreateNewReport !== 0) return false;
    setLimitWarningOpen(true);
    return true;
  }, [allowUserToCreateNewReport]);

  // Snackbar — single channel for all success / error messages.
  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
    id: ''
  });

  // Clear menu/selection when the drawer closes (matches LocationList).
  useEffect(() => {
    setOnCloseCallback(() => () => {
      setSelectedRow([]);
      setAnchorEl(null);
      setMenuRow(null);
    });
  }, [setOnCloseCallback]);

  /**
   * Open the wizard drawer. `reportId === null` => Add mode.
   */
  const openWizard = useCallback(
    (reportId = null) => {
      setTitle(
        reportId
          ? intl.formatMessage({ id: 'editReport', defaultMessage: 'Edit Report' })
          : intl.formatMessage({ id: 'addReport', defaultMessage: 'Add Report' })
      );
      setDrawerContent(
        <Suspense fallback={<LoadingFallback />}>
          <AddEditReportWizard
            reportId={reportId}
            onSuccess={() => {
              setSnack({
                open: true,
                severity: 'success',
                message: reportId ? <FormattedMessage id="reportEditedSuccessfully" /> : <FormattedMessage id="reportSavedSuccessfully" />,
                id: 'reportTab-alert-saved'
              });
              setSelectedRow([]);
              setOrgDrawerOpen(false);
              setReloadKey((k) => k + 1);
            }}
          />
        </Suspense>
      );
      setOrgDrawerOpen(true);
    },
    [intl, setTitle, setDrawerContent, setOrgDrawerOpen]
  );

  /** Open pdf viewer overlay; keep View/Download drawer open so closing report returns to the list. */
  const handleOpenReportView = useCallback((row) => {
    if (!row?.url) return;
    setReportToView({ name: row.name, url: row.url });
  }, []);

  const handleReportViewLoadFailed = useCallback(() => {
    setSnack({
      open: true,
      severity: 'error',
      message: <FormattedMessage id="pdfNotLoad" defaultMessage="Something went wrong. Please try again." />,
      id: 'reportTab-alert-pdf-not-load'
    });
  }, []);

  const openViewDownloadDrawer = useCallback(
    (reportId) => {
      if (!reportId) return;
      setTitle(
        intl.formatMessage({
          id: 'viewDownloadReport',
          defaultMessage: 'View/Download Report'
        })
      );
      setDrawerContent(
        <Suspense fallback={<LoadingFallback />}>
          <ViewDownloadReportDialog reportId={reportId} onClose={() => setOrgDrawerOpen(false)} onViewReport={handleOpenReportView} />
        </Suspense>
      );
      setOrgDrawerOpen(true);
    },
    [intl, setTitle, setDrawerContent, setOrgDrawerOpen, handleOpenReportView]
  );

  // ---------------- toolbar handlers ---------------- //
  const handleAdd = () => {
    if (showReportLimitWarningIfNeeded()) return;
    openWizard(null);
  };

  // Whether the single bulk-selected row is a CP report — or its type can't be
  // resolved. Selection persists across pages, so the row may not be on
  // currentPageRows; fall back to the accumulated cross-page map, and fail
  // closed (treat unknown as CP) so an off-page CP report can't slip through
  // the CP-disabled guard.
  const bulkSelectedIsCpOrUnknown = () => {
    const key = selectedRow[0] != null ? String(selectedRow[0]) : null;
    if (key == null) return false;
    const onPage = currentPageRows.find((r) => r.id === key);
    if (onPage) return !!onPage.isCaptivePortal;
    if (isCaptivePortalByIdRef.current.has(key)) {
      return isCaptivePortalByIdRef.current.get(key);
    }
    return true;
  };

  const handleEdit = (row) => {
    // Block editing Captive Portal reports while the feature is disabled — the
    // wizard's CP save path is gated off, so editing one must not be reachable
    // (including an off-page bulk selection).
    if (cpReportsDisabled) {
      const blocked = row ? !!row.isCaptivePortal : bulkSelectedIsCpOrUnknown();
      if (blocked) return;
    }
    if (row && row.reportId) {
      openWizard(row.reportId);
      return;
    }
    if (selectedRow.length === 1) openWizard(selectedRow[0]);
  };

  const handleDelete = (row) => {
    if (row && row.id) {
      // Row action delete — keep selection state separate so non-selectable
      // rows (e.g. browsing history) never appear checked in the grid.
      setDeleteTargetIds([String(row.id)]);
      setDeleteDialogOpen(true);
      return;
    }
    if (selectedRow.length >= 1) {
      setDeleteTargetIds([...selectedRow]);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetIds([]);
  };

  const handleCopy = (row) => {
    if (showReportLimitWarningIfNeeded()) return;
    const target = row || currentPageRows.find((r) => selectedRow[0] && r.id === String(selectedRow[0]));
    if (!target) return;
    setCopyTarget({ id: target.reportId, name: target.reportName });
  };

  // ---------------- per-row action menu ---------------- //
  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleRowCopy = () => {
    const row = menuRow;
    handleMenuClose();
    if (row) handleCopy(row);
  };
  const handleRowEmail = () => {
    const row = menuRow;
    handleMenuClose();
    if (!row) return;
    setEmailTarget({ id: row.reportId, emails: row.emails || [] });
  };
  const handleRowView = () => {
    const row = menuRow;
    handleMenuClose();
    if (row?.reportId) openViewDownloadDrawer(row.reportId);
  };

  /**
   * Download a captive-portal report. The PDF lives in the job-service's object
   * store (not AppCom's pdfList), so we mint a FRESH presigned URL on every
   * click (they expire after ~30 min) and open it directly.
   */
  const handleCaptivePortalDownload = useCallback(
    async (row) => {
      const jobId = row?.cpJobId;
      if (!jobId) {
        setSnack({
          open: true,
          severity: 'error',
          message: <FormattedMessage id="reportNotAvailableYet" defaultMessage="Report is not available yet." />,
          id: 'reportTab-alert-cp-no-job'
        });
        return;
      }
      showLoader();
      try {
        const url = await getCaptivePortalDownloadUrl(jobId);
        hideLoader();
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          setSnack({
            open: true,
            severity: 'warning',
            message: (
              <FormattedMessage
                id="reportNotReadyForDownload"
                defaultMessage="The report is still being generated. Please try again once it is completed."
              />
            ),
            id: 'reportTab-alert-cp-not-ready'
          });
        }
      } catch (err) {
        hideLoader();
        setSnack({
          open: true,
          severity: 'error',
          message: err?.message || <FormattedMessage id="pdfNotLoad" defaultMessage="Something went wrong. Please try again." />,
          id: 'reportTab-alert-cp-download-error'
        });
      }
    },
    [showLoader, hideLoader]
  );

  const handleRowCaptivePortalDownload = () => {
    const row = menuRow;
    handleMenuClose();
    if (row) handleCaptivePortalDownload(row);
  };

  const handleRowDownload = async () => {
    const row = menuRow;
    handleMenuClose();
    if (!row?.reportId) return;

    showLoader();
    try {
      const data = await downloadBrowsingHistoryReport(row.reportId);
      if (data == null || data === undefined) {
        setSnack({
          open: true,
          severity: 'error',
          message: intl.formatMessage({
            id: 'failedToLoadGeneratedReports',
            defaultMessage: 'Failed to load generated reports.'
          }),
          id: 'reportTab-alert-download-failed'
        });
        return;
      }

      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/zip' });

      if (blob.type && blob.type.indexOf('json') !== -1) {
        const text = await blob.text();
        let errMsg = intl.formatMessage({
          id: 'noGeneratedReportCommon',
          defaultMessage: 'No reports available for download.'
        });
        try {
          const json = JSON.parse(text);
          if (json?.response?.message) errMsg = json.response.message;
          else if (json?.message) errMsg = json.message;
        } catch (_) {
          // keep default message
        }
        setSnack({
          open: true,
          severity: 'error',
          message: errMsg,
          id: 'reportTab-alert-download-failed'
        });
        return;
      }

      triggerBrowsingHistoryZipDownload(blob, formatBrowsingHistoryZipName(row.reportName));
    } catch (error) {
      setSnack({
        open: true,
        severity: 'error',
        message:
          error?.message ||
          intl.formatMessage({
            id: 'failedToLoadGeneratedReports',
            defaultMessage: 'Failed to load generated reports.'
          }),
        id: 'reportTab-alert-download-error'
      });
    } finally {
      hideLoader();
    }
  };
  const handleRowDelete = () => {
    const row = menuRow;
    handleMenuClose();
    if (row) handleDelete(row);
  };

  // ---------------- delete flow ---------------- //
  /**
   * Bulk-delete the selected reports in a single API call (mirrors the
   * device-list pattern in `views/pages/devicePage/index.jsx`). The v2
   * endpoint accepts the full array of reportTemplateIds and returns
   * `details.successDeleted` / `details.notFound` so we can show an
   * accurate full / partial / failure message.
   */
  const handleConfirmDelete = async () => {
    const ids = Array.isArray(deleteTargetIds) ? [...deleteTargetIds] : [];
    if (ids.length === 0) {
      setDeleteDialogOpen(false);
      return;
    }
    const totalCount = ids.length;

    // Capture recurring scheduleIds for the rows being deleted BEFORE selection /
    // menu state is cleared, so a successful delete can cancel their crons. Pull
    // from the cross-page accumulator (covers off-page bulk selections), falling
    // back to the action-menu row. Only scheduled CP rows carry a cpScheduleId.
    const scheduleIdByReportId = new Map();
    ids.forEach((id) => {
      const key = String(id);
      let sid = scheduleIdByReportIdRef.current.get(key) || '';
      if (!sid && menuRow?.id != null && String(menuRow.id) === key) {
        sid = menuRow.cpScheduleId || '';
      }
      if (sid) scheduleIdByReportId.set(key, sid);
    });

    showLoader();
    try {
      const { response, details } = await deleteReport(ids);
      setDeleteDialogOpen(false);
      setDeleteTargetIds([]);
      setSelectedRow([]);
      setAnchorEl(null);
      setMenuRow(null);
      hideLoader();

      // Identical branching to `views/pages/devicePage/index.jsx >
      // handleConfirmDelete` — only the locale keys / labels and the
      // snackbar setter shape differ (this page uses `setSnack` instead of
      // `setShowSnackbar`).
      if (response && response.status) {
        // Best-effort: cancel recurring schedules for the deleted scheduled CP
        // reports so their crons stop firing (AppCom report-delete removes only the
        // list record; the insight-job-service schedule is separate). When the
        // response itemizes which ids were deleted, cancel ONLY those. Fall back to
        // "cancel all targeted" only when the response carries NO per-id detail at
        // all (neither success nor failure arrays) — otherwise a partial/total
        // failure (failureDeleted present, successDeleted empty) would wrongly
        // deactivate crons for reports that were not deleted. Failures are ignored.
        if (scheduleIdByReportId.size > 0) {
          const deletedOk = new Set((details?.successDeleted || []).map(String));
          const hasPerIdDetail = Array.isArray(details?.successDeleted) || Array.isArray(details?.failureDeleted);
          const cancelIds = ids
            .filter((id) => (hasPerIdDetail ? deletedOk.has(String(id)) : true))
            .map((id) => scheduleIdByReportId.get(String(id)))
            .filter(Boolean);
          if (cancelIds.length > 0) {
            await Promise.allSettled([...new Set(cancelIds)].map((sid) => cancelCaptivePortalSchedule(sid)));
          }
        }
        const successCount = details?.successDeleted?.length || 0;
        const notFoundCount = details?.failureDeleted?.length || 0;
        if (successCount === totalCount && totalCount > 0) {
          setSnack({
            open: true,
            severity: 'success',
            message:
              totalCount === 1 ? (
                <FormattedMessage id="reportDeletedSuccessSingle" />
              ) : (
                <FormattedMessage id="reportDeletedSuccessMultiple" values={{ count: totalCount }} />
              ),
            id: 'reportTab-alert-deleted'
          });
        } else if (successCount > 0 && notFoundCount > 0) {
          setSnack({
            open: true,
            severity: 'warning',
            message: intl.formatMessage(
              {
                id: 'reportDeletedPartial',
                defaultMessage:
                  "{successCount} out of {totalCount} reports got deleted Successfully. One or more reports have some issues or can't be deleted."
              },
              { successCount, totalCount }
            ),
            id: 'reportTab-alert-deleted-partial'
          });
        } else if (successCount === 0 && notFoundCount > 0) {
          setSnack({
            open: true,
            severity: 'error',
            message: intl.formatMessage({
              id: 'reportDeleteFailed',
              defaultMessage: 'None of the selected reports could be deleted.'
            }),
            id: 'reportTab-alert-delete-failed'
          });
        } else {
          setSnack({
            open: true,
            severity: 'success',
            message: intl.formatMessage({
              id: 'reportDeletedSuccessSingle'
            }),
            id: 'reportTab-alert-delete-info'
          });
        }
        setReloadKey((k) => k + 1);
      } else if (String(response?.responseCode) === REPORT_ALREADY_DELETED_CODE) {
        // The report is already gone — typically deleted from another session
        // while this list still showed it. Say so, and reload so the row drops.
        setSnack({
          open: true,
          severity: 'warning',
          message:
            totalCount === 1 ? (
              <FormattedMessage
                id="reportAlreadyDeletedSingle"
                defaultMessage="This report has already been deleted, possibly from another session. The list has been refreshed."
              />
            ) : (
              <FormattedMessage
                id="reportAlreadyDeletedMultiple"
                defaultMessage="One or more of the selected reports have already been deleted, possibly from another session. The list has been refreshed."
              />
            ),
          id: 'reportTab-alert-delete-already-deleted'
        });
        setReloadKey((k) => k + 1);
      } else {
        setSnack({
          open: true,
          severity: 'error',
          message: response?.message || 'Failed to delete report(s).',
          id: 'reportTab-alert-delete-error'
        });
      }
    } catch (error) {
      hideLoader();
      setDeleteDialogOpen(false);
      setDeleteTargetIds([]);
      setSelectedRow([]);
      setAnchorEl(null);
      setMenuRow(null);
      setSnack({
        open: true,
        severity: 'error',
        message:
          error?.message ||
          (totalCount === 1 ? <FormattedMessage id="reportDeleteFailed" /> : <FormattedMessage id="reportDeleteFailedMultiple" />),
        id: 'reportTab-alert-delete-exception'
      });
    }
  };

  // ---------------- columns ---------------- //
  const columns = [
    {
      field: 'reportName',
      headerName: 'Report Name',
      headerNameIntlId: 'reportNameCommon',
      flex: 1,
      width: 200,
      pinnable: true,
      filterColumnName: 'name',
      renderHeader: () => (
        <Typography component="span" sx={{ fontWeight: 500 }} id="reportTab-col-reportName">
          <FormattedMessage id="reportNameCommon" />
        </Typography>
      ),
      renderCell: (params) => <EllipsisTooltip placement="top">{params.row.reportName}</EllipsisTooltip>
    },
    {
      field: 'templateType',
      headerName: 'Type',
      headerNameIntlId: 'type',
      flex: 1,
      width: 200,
      pinnable: true,
      sortable: false,
      filterable: true,
      filterColumnName: 'templateType',
      filtersValue: [
        {
          1: 'Troubleshooting',
          2: 'Custom Report',
          3: 'Browsing History',
          4: 'Captive Portal'
        }
      ],
      renderHeader: () => (
        <Typography sx={{ fontWeight: 500 }} id="reportTab-col-templateType">
          <FormattedMessage id="type" defaultMessage="Type" />
        </Typography>
      ),
      renderCell: (params) => <EllipsisTooltip placement="top">{params.row.templateType || 'N/A'}</EllipsisTooltip>
    },
    {
      field: 'description',
      headerName: 'Description',
      headerNameIntlId: 'description',
      flex: 1,
      width: 200,
      pinnable: true,
      filterColumnName: 'description',
      renderHeader: () => (
        <Typography sx={{ fontWeight: 500 }} id="reportTab-col-description">
          <FormattedMessage id="description" />
        </Typography>
      ),
      // An empty description is only shown as "N/A" by the cell — export matches.
      csvValueGetter: (row) => row?.description || 'N/A',
      renderCell: (params) => <EllipsisTooltip placement="top">{params.row.description || 'N/A'}</EllipsisTooltip>
    },
    {
      field: 'schedule',
      headerName: 'Schedule',
      headerNameIntlId: 'schedule',
      flex: 1,
      width: 130,
      pinnable: true,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Typography sx={{ fontWeight: 500 }} id="reportTab-col-schedule">
          <FormattedMessage id="schedule" />
        </Typography>
      )
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      headerNameIntlId: 'created-on',
      flex: 1,
      width: 200,
      pinnable: true,
      filterable: false,
      filterColumnName: 'createdAt',
      renderHeader: () => (
        <Typography sx={{ fontWeight: 500 }} id="reportTab-col-createdOn">
          <FormattedMessage id="created-on" />
        </Typography>
      )
    },
    {
      field: 'lastReport',
      headerName: 'Last Report',
      headerNameIntlId: 'lastReport',
      flex: 1,
      width: 200,
      pinnable: true,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Typography sx={{ fontWeight: 500 }} id="reportTab-col-lastReport">
          <FormattedMessage id="lastReport" />
        </Typography>
      ),
      // Captive-portal reports have no "last scheduled run":
      //  - scheduled (recurring) rows store a scheduleId, not a job id, so we
      //    must NOT poll job status — show a static "Scheduled" chip.
      //  - one-shot rows show the async job's live status (polled).
      // The chip text exists only in renderCell — the export rebuilds it.
      csvPrepare: prepareCpStatusesForExport,
      csvValueGetter: (row) => getLastReportExportValue(row, intl, cpStateByJobIdRef.current),
      renderCell: (params) => {
        if (!params.row.isCaptivePortal) {
          return <EllipsisTooltip placement="top">{params.row.lastReport}</EllipsisTooltip>;
        }
        if (isScheduledCaptivePortalRow(params.row)) {
          return (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={<FormattedMessage id="scheduledCommon" defaultMessage="Scheduled" />}
              id={`reportList-cpScheduled-${params.row.id}`}
            />
          );
        }
        return <CaptivePortalStatusCell jobId={params.row.cpJobId} initialStatus={params.row.cpLastStatus} />;
      }
    },
    {
      field: 'nextReport',
      headerName: 'Next Report',
      headerNameIntlId: 'nextReport',
      flex: 1,
      width: 200,
      pinnable: true,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Typography sx={{ fontWeight: 500 }} id="reportTab-col-nextReport">
          <FormattedMessage id="nextReport" />
        </Typography>
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      resizable: false,
      headerClassName: 'no-resize-column',
      width: 70,
      sortable: false,
      filterable: false,
      renderHeader: () => <Typography id="reportTab-col-actions" />,
      renderCell: (params) => {
        const isActionDisabled = selectedRow.length > 1;
        const isBrowsingHistory = isBrowsingHistoryRow(params.row);
        return (
          <>
            {/* Edit: every report type except browsing-history (read-only).
                  Captive Portal rows are not editable while the CP feature is
                  disabled (creation is also blocked in ReportProfileStep). */}
            {!isBrowsingHistory && !(cpReportsDisabled && params.row.isCaptivePortal) && (
              <EditActionButton
                onClick={() => {
                  handleEdit(params.row);
                  setAnchorEl(null);
                }}
                disabled={isActionDisabled}
                id={`reportList-actionMenu-edit-${params.row.id}`}
              />
            )}
            <Box
              onClick={(e) => !isActionDisabled && handleMenuOpen(e, params.row)}
              id={`reportList-actionMenu-${params.row.id}`}
              sx={{
                opacity: isActionDisabled ? 0.3 : 1,
                pointerEvents: isActionDisabled ? 'none' : 'auto',
                cursor: isActionDisabled ? 'default' : 'pointer',
                display: 'flex'
              }}
            >
              <MoreVertIcon id={`reportList-moreVertIcon-${params.row.id}`} />
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && menuRow?.id === params.row.id}
              onClose={handleMenuClose}
              sx={{ fontSize: 12 }}
              id={`reportList-actionMenuPopover-${params.row.id}`}
            >
              {params.row.isCaptivePortal ? (
                // Captive-portal one-shot report: download fresh presigned URL from
                // insight-job-service. Scheduled CP rows hold a scheduleId (not a
                // jobId) so the download endpoint would fail — hide it for those.
                params.row.schedule && !['N/A', 'Run Once', ''].includes(params.row.schedule) ? null : (
                  <MenuItem
                    sx={{ fontSize: 13 }}
                    onClick={handleRowCaptivePortalDownload}
                    id={`reportList-actionMenu-cpDownload-${params.row.id}`}
                  >
                    <FormattedMessage id="download" defaultMessage="Download" />
                  </MenuItem>
                )
              ) : isBrowsingHistory ? (
                // Browsing-history report: zip download only (no copy/email/view).
                <MenuItem sx={{ fontSize: 13 }} onClick={handleRowDownload} id={`reportList-actionMenu-download-${params.row.id}`}>
                  <FormattedMessage id="download" defaultMessage="Download" />
                </MenuItem>
              ) : (
                [
                  <MenuItem key="copy" sx={{ fontSize: 13 }} onClick={handleRowCopy} id={`reportList-actionMenu-copy-${params.row.id}`}>
                    <FormattedMessage id="copyReport" />
                  </MenuItem>,
                  <MenuItem key="email" sx={{ fontSize: 13 }} onClick={handleRowEmail} id={`reportList-actionMenu-email-${params.row.id}`}>
                    <FormattedMessage id="emailReportCommon" />
                  </MenuItem>,
                  <MenuItem key="view" sx={{ fontSize: 13 }} onClick={handleRowView} id={`reportList-actionMenu-view-${params.row.id}`}>
                    <FormattedMessage id="viewDownload" defaultMessage="View/Download" />
                  </MenuItem>
                ]
              )}
              <MenuItem
                sx={{ fontSize: 13 }}
                className="MuiMenuItem-colorDanger"
                onClick={handleRowDelete}
                id={`reportList-actionMenu-delete-${params.row.id}`}
              >
                <FormattedMessage id="delete" />
              </MenuItem>
            </Menu>
          </>
        );
      }
    }
  ];

  // ---------------- selection plumbing (mirrors LocationList) ---------------- //
  const handleRowSelectionModelChange = (selectionModel, data) => {
    if (selectionModel?.type === 'include') {
      const idsArray = Array.from(selectionModel.ids || []).map((id) => String(id));
      const pageIds = (data || []).map((row) => String(row?.id ?? ''));
      if (idsArray.length === 0) {
        if (justChangedPageRef.current) return;
        setSelectedRow((prev) => {
          const overlap = prev.some((id) => pageIds.includes(String(id)));
          if (!overlap) return prev;
          return prev.filter((id) => !pageIds.includes(String(id)));
        });
        return;
      }
      setSelectedRow((prev) => {
        const others = prev.map(String).filter((id) => !pageIds.includes(id));
        return [...new Set([...others, ...idsArray])];
      });
    } else if (selectionModel?.type === 'exclude') {
      setSelectedRow(currentPageIds.map(String));
    } else {
      setSelectedRow([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rowSelectionModel = React.useMemo(() => ({ type: 'include', ids: new Set(selectedRow) }), [selectedRow.join(',')]);

  const handleGridDataChange = (ids) => {
    justChangedPageRef.current = false;
    setCurrentPageIds((ids || []).map(String));
  };

  const handleGridRowsChange = (rows) => {
    setCurrentPageRows(rows || []);
    // Accumulate scheduleIds so a bulk delete that spans pages can still cancel
    // off-page scheduled CP rows. Most-recent value per id wins (re-merged each
    // time a page loads), so an edited schedule is reflected.
    (rows || []).forEach((r) => {
      if (r?.id != null && r?.cpScheduleId) {
        scheduleIdByReportIdRef.current.set(String(r.id), r.cpScheduleId);
      }
      if (r?.id != null) {
        isCaptivePortalByIdRef.current.set(String(r.id), !!r.isCaptivePortal);
      }
    });
  };

  const handlePaginationModelChange = () => {
    justChangedPageRef.current = true;
  };

  return (
    <>
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        id="reportTab-snackbar"
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
          id={snack.id || 'reportTab-alert'}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <MainCard id="reportTab-mainCard">
        <SmartDataGrid
          noRowsMessageId="noReportsFound"
          apiConfig={apiConfig}
          columns={columns}
          key={reloadKey}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          onPaginationModelChange={handlePaginationModelChange}
          onDataChange={handleGridDataChange}
          onRowsChange={handleGridRowsChange}
          isRowSelectable={(params) => !isBrowsingHistoryRow(params.row)}
          id="reportTab-smartDataGrid"
          initialSortModel={[{ field: 'reportName', sort: 'asc' }]}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={selectedRow.length >= 1}
            id="reportList-addBtn"
            data-testid="reports-add-btn"
          >
            <FormattedMessage id="add" />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEdit()}
            disabled={selectedRow.length !== 1 || (cpReportsDisabled && bulkSelectedIsCpOrUnknown())}
            id="reportList-editBtn"
            data-testid="reports-edit-btn"
          >
            <FormattedMessage id="edit" />
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleDelete()}
            disabled={selectedRow.length < 1}
            id="reportList-deleteBtn"
            data-testid="reports-delete-btn"
          >
            <FormattedMessage id="delete" />
          </Button>
        </SmartDataGrid>
      </MainCard>

      {/* Report generation limit warning */}
      <ConfirmDialog
        open={limitWarningOpen}
        onClose={() => setLimitWarningOpen(false)}
        title={<FormattedMessage id="warning" defaultMessage="Warning" />}
        message={getReportLimitWarningMessage()}
        confirmText={<FormattedMessage id="okCommon" defaultMessage="OK" />}
        hideCancel
        onConfirm={() => setLimitWarningOpen(false)}
        id="reportTab-limitWarningDialog"
        confirmButtonId="reportTab-limitWarningDialogOkBtn"
        confirmVariant="contained"
        image={<img src={cancelAutoRenewalImg} alt="Report Limit Warning" style={{ width: '47px' }} />}
        imgBgColor="error"
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        title={<FormattedMessage id="deleteReportTemplate" />}
        message={
          <FormattedMessage
            id={deleteTargetIds.length === 1 ? 'deleteReportConfirmSingle' : 'deleteReportConfirmMultiple'}
            values={{ count: deleteTargetIds.length }}
          />
        }
        confirmText={<FormattedMessage id="delete" />}
        cancelText={<FormattedMessage id="no" />}
        onConfirm={handleConfirmDelete}
        id="reportTab-confirmDialog"
        confirmButtonId="reportTab-deleteDialogConfirmBtn"
        cancelButtonId="reportTab-deleteDialogCancelBtn"
        confirmVariant="contained"
        image={<img src={cancelAutoRenewalImg} alt="Delete Confirmation" style={{ width: '47px' }} />}
        imgBgColor="error"
      />

      {/* Copy Report dialog */}
      <Suspense fallback={null}>
        <CopyReportDialog
          open={Boolean(copyTarget)}
          onClose={() => setCopyTarget(null)}
          reportId={copyTarget?.id}
          reportName={copyTarget?.name}
          onSuccess={() => {
            setSnack({
              open: true,
              severity: 'success',
              message: <FormattedMessage id="reportCopiedSuccess" />,
              id: 'reportTab-alert-copied'
            });
            setSelectedRow([]);
            setReloadKey((k) => k + 1);
          }}
        />
      </Suspense>

      {/* Email Report dialog */}
      <Suspense fallback={null}>
        <EmailReportDialog
          open={Boolean(emailTarget)}
          onClose={() => setEmailTarget(null)}
          reportId={emailTarget?.id}
          emails={emailTarget?.emails}
          onSuccess={() => {
            setSnack({
              open: true,
              severity: 'success',
              message: <FormattedMessage id="reportEmailUpdatedSuccess" />,
              id: 'reportTab-alert-email-updated'
            });
            setReloadKey((k) => k + 1);
          }}
        />
      </Suspense>

      {/* Full-screen PDF viewer — classic list.js onOpenModal / pdfContainer */}
      <Suspense fallback={null}>
        <ViewReportDialog
          open={Boolean(reportToView?.url)}
          onClose={() => setReportToView(null)}
          onLoadFailed={handleReportViewLoadFailed}
          reportName={reportToView?.name}
          pdfUrl={reportToView?.url}
        />
      </Suspense>
    </>
  );
};

export default Reports;
